import { Link } from "react-router-dom";

import { Badge, Card, Table, Td, Th } from "../../../components/ui";
import { ResourceView } from "../../../components/states";
import type { AppConfig } from "../../../config";
import { useResource } from "../../../hooks/useResource";
import {
  effectiveDomainsOf,
  effectivePluginsOf,
  labeebTenantSettingsPath,
  type LabeebTenantSettings,
} from "../../../api/labeeb";
import { LabeebDomainControl } from "../../../components/labeeb/LabeebDomainControl";
import { LabeebMissing } from "./labeebMissing";

/**
 * Which DOMAINS this merchant may use.
 *
 * REPLACES the agents panel. Labeeb's new admin API has no agents at all —
 * the word does not appear once in its OpenAPI spec — and `enabled_domains`
 * on `GET /admin/tenant/{id}/settings` is what took their place.
 *
 * NOT itself collapsible. The product BLOCK is the collapsible unit; folding
 * its sections as well meant an operator who opened Labeeb saw one expanded
 * panel and four shut bars.
 *
 * The effective set is now READ, not derived. The old panel computed it by
 * intersecting the tenant's agents with an app-level ceiling and labelled the
 * result "derived", because no route returned it. `settings.effective` returns
 * it, per domain, with `restricted_by` naming whatever is holding one off —
 * so the console can finally say WHY a capability is unavailable instead of
 * only that it is.
 */
export function LabeebCapabilities({
  config,
  tenantId,
}: {
  config: AppConfig;
  tenantId: string;
}) {
  const settings = useResource<LabeebTenantSettings>(
    config,
    labeebTenantSettingsPath(tenantId),
  );
  const s = settings.state;

  return (
    <>
    <Card title="Domains">
      <ResourceView
        state={s}
        what="domains"
        onRetry={settings.reload}
        notFound={<LabeebMissing state={s} />}
      />

      {s.kind === "ready" &&
        (() => {
          const domains = effectiveDomainsOf(s.data);
          const plugins = effectivePluginsOf(s.data);
          const asked = s.data?.settings?.enabled_domains;

          return (
            <>
              {domains.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No domains are reported for this merchant.
                </p>
              ) : (
                <>
                  <Table
                    head={
                      <tr>
                        <Th>Domain</Th>
                        <Th>In force</Th>
                        <Th>Held off by</Th>
                      </tr>
                    }
                  >
                    {domains.map((dm) => (
                      <tr key={dm.domain}>
                        <Td>
                          <span className="ref">{dm.domain}</span>
                        </Td>
                        <Td>
                          {dm.enabled ? (
                            <Badge>on</Badge>
                          ) : (
                            <Badge tone="red">off</Badge>
                          )}
                        </Td>
                        <Td>
                          {dm.restricted_by ? (
                            <span className="text-xs text-amber-700 dark:text-amber-400">
                              {dm.restricted_by}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </Table>

                  <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                    This table is what RUNS for agent domains (menu, inventory, …). What the
                    merchant asked for is{" "}
                    <span className="ref">
                      {asked === undefined
                        ? "unset"
                        : typeof asked === "string"
                          ? asked
                          : asked.length === 0
                            ? "none"
                            : asked.join(", ")}
                    </span>
                    {" — "}the environment can still hold a domain off, and the column above
                    says when it does.
                  </p>
                </>
              )}

              {plugins.length > 0 && (
                <div className="mt-4">
                  <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                    Plugins (separate from domains)
                  </p>
                  <Table
                    head={
                      <tr>
                        <Th>Plugin</Th>
                        <Th>In force</Th>
                        <Th>Held off by</Th>
                      </tr>
                    }
                  >
                    {plugins.map((pl) => (
                      <tr key={pl.plugin}>
                        <Td>
                          <span className="ref">{pl.plugin}</span>
                        </Td>
                        <Td>
                          {pl.enabled ? <Badge>on</Badge> : <Badge tone="red">off</Badge>}
                        </Td>
                        <Td>
                          {pl.restricted_by ? (
                            <span className="text-xs text-amber-700 dark:text-amber-400">
                              {pl.restricted_by}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </Table>
                  <p className="mt-2 text-xs/5 text-slate-500 dark:text-slate-400">
                    Disabling a plugin for every business is on{" "}
                    <Link className="underline" to="/policy/settings">
                      Policy → Settings
                    </Link>
                    . &quot;No domains&quot; here does not turn plugins off — with{" "}
                    <span className="ref">analytics</span> still on, BI-style questions can keep
                    working in the merchant Console.
                  </p>
                </div>
              )}

              <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                Changes apply from the business&apos;s next chat turn, not mid-turn. Domains do not
                suspend Labeeb — use Access → Suspend below to refuse every request. Plain replies
                without agent tools may still appear when all domains are off.
              </p>
            </>
          );
        })()}
      <LabeebDomainControl
        config={config}
        tenantId={tenantId}
        settings={s}
        reload={settings.reload}
      />
    </Card>
    </>
  );
}
